import { Component } from "@angular/core";
import { GetRowIdParams, GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { StrandedItem, StrandedJobOrder, StrandedOrderResult } from "@/model/entities/-stranded-order";
import { AgFns, GridOptionsExt } from "@/shared/ag-fns";
import { DomainBaseComponent } from "@/shared/domain-base.component";
import { DomainService } from "@/shared/domain.service";
import { NavFns } from "@/shared/nav-fns";

@Component({
  selector: "app-stranded-orders",
  templateUrl: "./stranded-orders.component.html",
  standalone: false,
})
export class StrandedOrdersComponent extends DomainBaseComponent {
  johGridOptions: GridOptionsExt;
  itemGridOptions: GridOptionsExt;

  result: StrandedOrderResult | null = null;
  jobOrders: StrandedJobOrder[] = [];
  items: StrandedItem[] = [];

  selectedJoHeaderId: number | null = null;
  isLoading = false;
  isClosing = false;

  constructor(protected domainService: DomainService) {
    super(domainService);

    this.johGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onJohGridReady,
      onRowSelected: this.onJohRowSelected,
      rowSelection: "single",
      getRowId: (p: GetRowIdParams) => String((p.data as StrandedJobOrder).JoHeaderId),
      getRowClass: (p: any) => ((p.data as StrandedJobOrder)?.HasOpenShipBin ? "rs-already-processed" : ""),
    });

    this.itemGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onItemGridReady,
      getRowId: (p: GetRowIdParams) => (p.data as StrandedItem).ItemDetailId,
      cellSelection: false,
    });

    this.isPageReady = true;
  }

  onJohGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: "J/O #",
        ...NavFns.createIdCellClickedNavProps("JoHeaderId", this.router, "/in-process/manage/job-orders"),
        type: "leftAligned",
        filter: "agNumberColumnFilter",
        ...AgFns.fixWidth(110),
      },
      { headerName: "Account", field: "AccountName", filter: "agTextColumnFilter", ...AgFns.flexWidth(200) },
      { headerName: "J/O Date", field: "JoDate", filter: "agDateColumnFilter", ...AgFns.fixWidth(120), valueFormatter: this.dateFormatter },
      { headerName: "Last Invoice", field: "LastInvoiceDate", filter: "agDateColumnFilter", ...AgFns.fixWidth(130), valueFormatter: this.dateFormatter },
      { headerName: "Days Stale", field: "DaysStale", type: "numericColumn", filter: "agNumberColumnFilter", ...AgFns.fixWidth(110) },
      { headerName: "Details", field: "DetailCount", type: "numericColumn", ...AgFns.fixWidth(90) },
      { headerName: "Invoices", field: "InvoiceCount", type: "numericColumn", ...AgFns.fixWidth(95) },
      { headerName: "Shipped", field: "ShippedQty", type: "numericColumn", ...AgFns.fixWidth(95) },
      { headerName: "On Voucher", field: "StrandedQty", type: "numericColumn", ...AgFns.fixWidth(115) },
      {
        headerName: "What To Do",
        colId: "action",
        filter: "agSetColumnFilter",
        ...AgFns.flexWidth(230),
        valueGetter: (p: any) => this.getAction(p.data as StrandedJobOrder),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "StrandedQty", sort: "desc" }]);
  }

  onItemGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: "J/O #", field: "JoHeaderId", type: "leftAligned", ...AgFns.fixWidth(110) },
      { headerName: "J/O Detail", field: "JoDetailId", type: "leftAligned", ...AgFns.fixWidth(120) },
      { headerName: "Item", field: "ItemDetailId", filter: "agTextColumnFilter", ...AgFns.fixWidth(150) },
      { headerName: "Manufacturer", field: "Manufacturer", filter: "agTextColumnFilter", ...AgFns.flexWidth(150) },
      { headerName: "SKU", field: "Sku", filter: "agTextColumnFilter", ...AgFns.flexWidth(120) },
      { headerName: "Description", field: "Description", filter: "agTextColumnFilter", ...AgFns.flexWidth(200) },
      { headerName: "Invoice", field: "InvoiceHeaderId", filter: "agTextColumnFilter", ...AgFns.fixWidth(140) },
      { headerName: "Invoice Status", field: "InvoiceStatusId", filter: "agSetColumnFilter", ...AgFns.fixWidth(140) },
      { headerName: "QuickBooks Id", field: "QbInvoiceId", ...AgFns.fixWidth(140) },
      { headerName: "Ship Bin", field: "ShipBinId", type: "leftAligned", ...AgFns.fixWidth(100) },
      {
        headerName: "Bin Is Open",
        colId: "binOpen",
        ...AgFns.fixWidth(120),
        valueGetter: (p: any) => ((p.data as StrandedItem)?.IsShipBinOpen ? "Yes" : "No"),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: "JoHeaderId", sort: "asc" }]);
  }

  async runSearch() {
    this.isLoading = true;
    try {
      const result = await this.dbQueryService.findStrandedOrders();
      this.result = result;
      this.jobOrders = result.JobOrders ?? [];
      this.selectedJoHeaderId = null;
      this.applyItemFilter();

      if (result.JobOrderCount === 0) {
        this.toastr.success("The system found no stranded job orders.", "Stranded Job Orders");
      } else {
        this.toastr.warning(
          `The system found ${result.JobOrderCount} stranded job orders and ${result.StrandedItemCount} stranded items.`,
          "Stranded Job Orders",
        );
      }
    } catch (err: any) {
      this.toastr.error(err.message || "The system did not finish. Select Populate to try again.", "Stranded Job Orders");
    } finally {
      this.isLoading = false;
    }
  }

  /** Closes every listed order that is complete. The server checks each one again before it
   * closes it, so an order that changed since Populate is refused, not closed. */
  async closeCompleteOrders() {
    const orders = this.closableOrders;
    if (!orders.length) return;
    const itemCount = orders.reduce((sum, o) => sum + o.StrandedQty, 0);
    const itemText = itemCount > 0 ? ` The system first sets ${itemCount} items on a voucher to Shipped.` : "";
    const ok = await this.dialogService.yesNoDialog(
      "Close Complete Orders",
      `Close ${orders.length} job orders that are fully shipped and fully invoiced?${itemText}`,
    );
    if (!ok) return;

    this.isClosing = true;
    try {
      const result = await this.dbQueryService.closeCompleteStrandedOrders(orders.map((o) => o.JoHeaderId));
      if (result.ClosedIds.length > 0) {
        this.toastr.success(`The system closed ${result.ClosedIds.length} job orders.`, "Stranded Job Orders");
      }
      if (result.Refused.length > 0) {
        const lines = result.Refused.map((r) => `${r.JoHeaderId}: ${r.Reason}`).join("<br>");
        this.toastr.warning(`The system did not close ${result.Refused.length} job orders.<br>${lines}`, "Stranded Job Orders", {
          disableTimeOut: true,
        });
      }
    } catch (err: any) {
      this.toastr.error(err.message || "The system did not close the job orders.", "Stranded Job Orders");
    } finally {
      this.isClosing = false;
    }
    await this.runSearch();
  }

  onJohRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    this.selectedJoHeaderId = (event.data as StrandedJobOrder)?.JoHeaderId ?? null;
    this.applyItemFilter();
  }

  showAllItems() {
    this.selectedJoHeaderId = null;
    this.johGridOptions.apiX?.deselectAll();
    this.applyItemFilter();
  }

  private applyItemFilter() {
    const all = this.result?.Items ?? [];
    this.items = this.selectedJoHeaderId == null ? all : all.filter((i) => i.JoHeaderId === this.selectedJoHeaderId);
  }

  /** Mirrors the refusals in StrandedOrderService.CloseCompleteOrders. */
  private isClosable(joh: StrandedJobOrder) {
    return !joh.HasOpenShipBin && joh.AwaitPostInvoices === 0 && joh.InvoicesWithoutQbId === 0;
  }

  private getAction(joh: StrandedJobOrder) {
    if (!joh) return "";
    if (joh.HasOpenShipBin) return "Close the ship bin first";
    if (joh.AwaitPostInvoices > 0) return "Post the invoice first";
    if (joh.InvoicesWithoutQbId > 0) return "Examine the invoice first";
    return joh.StrandedQty > 0 ? "Set items to Shipped, then close" : "Close the job order";
  }

  private dateFormatter(p: any) {
    return p.value ? new Date(p.value).toLocaleDateString() : "";
  }

  get closableOrders(): StrandedJobOrder[] {
    return this.jobOrders.filter((o) => this.isClosable(o));
  }

  get hasRun(): boolean {
    return this.result != null;
  }

  get itemCountLabel(): string {
    if (this.selectedJoHeaderId == null) return "All stranded items";
    return `Stranded items for job order ${this.selectedJoHeaderId}`;
  }
}
